import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';

import { products } from '../../../shared/data/product-data';
import { BlueOcean } from '../../../shared/models/blueocean-product';
import { PowerBiApi } from '../models/power-bi-models';
import { PowerBIAPI } from '../api/power-bi-api.service';
import { ApplicationState } from '../../../shared/state/application-state.service';

@Injectable({ providedIn: 'root' })
export class PowerBiState {

  private readonly api = inject(PowerBIAPI);
  private readonly appState = inject(ApplicationState);

  readonly products = products;

  private readonly workspaceStorageKey = 'activeWorkspaceId';
  private readonly reportStorageKey = 'activeReportId';

  // Optional route/navigation selection.
  // Can contain: workspace_id, exact workspace name, workspace display name, environment id contained in the workspace name
  readonly requestedWorkspace = signal<string | null>(null);
  // Optional route/navigation selection for reports. Can contain report id or report name.
  readonly requestedReport = signal<string | null>(null);

  // Selection 
  readonly selectedWorkspaceId = signal<string | null>(null);
  readonly selectedWorkspace = computed(() => {
    const workspaceId = this.selectedWorkspaceId();
    if (!workspaceId) return null;
    return this.workspaces().find(workspace => workspace.id === workspaceId) ?? null;
  });
  readonly selectedReportId = signal<string | null>(null);
  readonly selectedReport = computed(() => {
    const reportId = this.selectedReportId();
    if (!reportId) return null;
    return this.reports().find(report => report.id === reportId) ?? null;
  });

  // State

  // Workspaces
  readonly workspacesResource = rxResource({
    params: () => {
      const account = this.appState.activeAccount();
      return account ? { accountId: account.homeAccountId } : undefined;
    },
    stream: () => this.api.getWorkspaces(),
  });
  readonly workspaces = computed(() => this.workspacesResource.value()?.value.map(value => ({ ...value, normalizedName: this.normalizeWorkspaceName(value.name) })) ?? []);
  readonly loadingWorkspaces = this.workspacesResource.isLoading;
  readonly workspacesError = computed(() => this.getErrorMessage(this.workspacesResource.error()));
  // Reports
  readonly reportsResource = rxResource({
    params: () => {
      const workspaceId = this.selectedWorkspaceId();
      return workspaceId ? { workspaceId } : undefined;
    },
    stream: ({ params }) => this.api.getReports(params.workspaceId)
  });
  readonly reports = computed(() => this.reportsResource.value()?.value ?? []);
  readonly loadingReports = this.reportsResource.isLoading;
  readonly reportsError = computed(() => this.getErrorMessage(this.reportsResource.error()));

  // Products
  readonly boughtProductNames = computed(() => new Set(this.reports().map(report => report.name.toLowerCase())));
  readonly availableNotBoughtProducts = computed(() => {
    const bought = this.boughtProductNames();
    return this.products.filter(product => !bought.has(product.name.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name));
  });

  constructor() {
    this.registerWorkspaceSelectionEffect();
    this.registerReportSelectionEffect();
  }

  // workspace selection
  private registerWorkspaceSelectionEffect(): void {
    effect(() => {
      const workspaces = this.workspaces();
      const requested = this.requestedWorkspace();
      if (!workspaces.length) {
        untracked(() => this.selectedWorkspaceId.set(null));
        return;
      }
      const current = untracked(() => this.selectedWorkspaceId());
      // Explicit route/navigation selection always wins.
      if (requested) {
        const requestedWorkspace = this.findWorkspace(requested);
        if (requestedWorkspace && requestedWorkspace.id !== current) {
          untracked(() => this.applyWorkspaceSelection(requestedWorkspace.id));
        }
        if (requestedWorkspace) return;
      }
      // Keep current selection if still valid.
      if (current && workspaces.some(workspace => workspace.id === current)) return;
      // Otherwise try persisted selection.
      const storedWorkspaceId = localStorage.getItem(this.workspaceStorageKey);
      const storedWorkspace = storedWorkspaceId ? workspaces.find(workspace => workspace.id === storedWorkspaceId) : null;
      if (storedWorkspace) {
        untracked(() => this.applyWorkspaceSelection(storedWorkspace.id));
        return;
      }
      // Final fallback: default to the first workspace in the list.
      untracked(() => this.applyWorkspaceSelection(workspaces[0].id));
    });
  }

  private applyWorkspaceSelection(workspaceId: string): void {
    if (this.selectedWorkspaceId() === workspaceId) return;
    this.selectedWorkspaceId.set(workspaceId);
    this.selectedReportId.set(null);
    localStorage.setItem(this.workspaceStorageKey, workspaceId);
  }

  // Report Selection
  private registerReportSelectionEffect(): void {
    effect(() => {
      const workspaceId = this.selectedWorkspaceId();
      const reports = this.reports();
      const requested = this.requestedReport();
      if (!workspaceId || this.reports().length === 0) {
        untracked(() => this.selectedReportId.set(null));
        return;
      }
      const current = untracked(() => this.selectedReportId());
      // Explicit route/navigation selection wins.
      if (requested) {
        const requestedReport = this.findReport(requested);
        if (requestedReport && requestedReport.id !== current) {
          untracked(() => this.applyReportSelection(requestedReport.id));
        }
        if (requestedReport) return;
      }
      // Keep current report if it exists in this workspace.
      if (current && reports.some(report => report.id === current)) return;
      // Try persisted report.
      const storedReportId = localStorage.getItem(this.reportStorageKey);
      const storedReport = storedReportId ? reports.find(report => report.id === storedReportId) : null;
      if (storedReport) {
        untracked(() => this.applyReportSelection(storedReport.id));
        return;
      }
      // no automatic report selection if none is requested or persisted. ( /reports route page is responsible for handling this )
    });
  }

  private applyReportSelection(reportId: string): void {
    this.selectedReportId.set(reportId);
    localStorage.setItem(this.reportStorageKey, reportId);
  }
  // public methods
  requestWorkspace(workspace: string | null): void {
    this.requestedWorkspace.set(workspace);
  }
  requestReport(report: string | null): void {
    this.requestedReport.set(report);
  }
  clearRequestedSelection(): void {
    this.requestedWorkspace.set(null);
    this.requestedReport.set(null);
  }
  selectWorkspace(workspaceId: string | null): void {
    this.requestedWorkspace.set(null);
    if (!workspaceId) {
      this.selectedWorkspaceId.set(null);
      this.selectedReportId.set(null);
      localStorage.removeItem(this.workspaceStorageKey);
      localStorage.removeItem(this.reportStorageKey);
      return;
    }

    this.applyWorkspaceSelection(workspaceId);
  }

  selectReport(reportId: string | null): void {
    this.requestedReport.set(null);

    if (!reportId) {
      this.selectedReportId.set(null);
      localStorage.removeItem(this.reportStorageKey);
      return;
    }

    const report = this.reports().find(report => report.id === reportId);
    if (!report) return;

    this.applyReportSelection(report.id);
  }

  selectReportByName(reportName: string): void {
    const report = this.findReport(reportName);
    if (!report) return;

    this.selectReport(report.id);
  }

  // -------------------------------------------------------------------------
  // Lookup
  // -------------------------------------------------------------------------

  findWorkspace(value: string): PowerBiApi.Workspace | null {
    const search = this.normalize(value);
    return (
      this.workspaces().find(workspace => {
        const candidates = [
          workspace.id,
          workspace.name,
          workspace.displayName,
          this.normalizeWorkspaceName(
            workspace.name,
          ),
        ];
        return candidates.some(candidate => candidate && this.normalize(candidate) === search);
      }) ?? null
    );
  }

  findReport(value: string): PowerBiApi.Report | null {
    const search = this.normalize(value);
    return (this.reports().find(report => this.normalize(report.id) === search || this.normalize(report.name) === search) ?? null);
  }

  private normalizeWorkspaceName(value: string): string {
    if (!value) return value;
    if (!/^\d{6}[_-]/.test(value)) return value;
    // customer workspaces following <customer_id>_<environment_id>[_<type>]
    return value
      .replace(/^[0-9]+[_-]/, '') // remove <customer_id>
      .replace(/-\d+(?=[_-]|$)/g, '') // remove <environment_id> prefix
      .replace(/[_-]+/g, ' - ') // replace dashes -> underscores
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private normalize(value: string): string {
    return value.trim().toLowerCase();
  }

  // resource actions
  reloadWorkspaces(): void {
    this.workspacesResource.reload();
  }

  reloadReports(): void {
    this.reportsResource.reload();
  }

  refreshUserPermissions(): void {
    this.api
      .refreshUserPermissions()
      .subscribe({
        next: () => {
          this.reloadWorkspaces();
          if (this.selectedWorkspaceId()) {
            this.reloadReports();
          }
        },
      });
  }

  private getErrorMessage(error: unknown): string | null {
    if (!error) return null;
    if (error instanceof Error) return error.message;
    return 'An unexpected Power BI error occurred.';
  }
}